/**
 * Application Validation Schemas
 *
 * Validation schemas for application operations including creation,
 * updates, deployment, and status management.
 *
 * CRITICAL: This file is adapted from tenant-api/src/validators/application.ts
 * Note: Uses local enum imports instead of backend models
 */

import { z } from 'zod';
import { nonEmptyStringSchema, descriptionSchema, urlSchema } from './common';

// ============================================================================
// APPLICATION STATUS VALIDATION
// ============================================================================

/**
 * Application deployment status enum
 * Valid statuses: pending, deploying, deployed, failed
 */
export const ApplicationDeploymentStatus = {
  PENDING: 'pending',
  DEPLOYING: 'deploying',
  DEPLOYED: 'deployed',
  FAILED: 'failed',
} as const;

export type ApplicationDeploymentStatus = typeof ApplicationDeploymentStatus[keyof typeof ApplicationDeploymentStatus];

/**
 * Application deployment status enum validation
 */
export const applicationDeploymentStatusSchema = z.enum(['pending', 'deploying', 'deployed', 'failed'], {
  required_error: 'Application deployment status is required',
  invalid_type_error: 'Invalid application deployment status',
});

export type ApplicationDeploymentStatusValidation = z.infer<typeof applicationDeploymentStatusSchema>;

/**
 * Execution mode for deployments
 * - latest: Application automatically runs the most recently deployed code
 * - custom: Application runs a specific deployment selected manually
 */
export const ExecutionMode = {
  LATEST: 'latest',
  CUSTOM: 'custom',
} as const;

export type ExecutionMode = typeof ExecutionMode[keyof typeof ExecutionMode];

/**
 * Execution mode enum validation
 */
export const executionModeSchema = z.enum(['latest', 'custom'], {
  invalid_type_error: 'Invalid execution mode',
});

export type ExecutionModeValidation = z.infer<typeof executionModeSchema>;

// ============================================================================
// APPLICATION VERSION VALIDATION
// ============================================================================

/**
 * Semantic version validation schema
 * Format: major.minor.patch (e.g., 1.0.0, 2.1.3)
 * Also allows v prefix (e.g., v1.0.0)
 */
export const versionSchema = z
  .string({
    invalid_type_error: 'Version must be a string',
  })
  .regex(/^v?\d+\.\d+\.\d+$/, 'Version must follow semantic versioning format (e.g., 1.0.0 or v1.0.0)')
  .optional();

export type VersionValidation = z.infer<typeof versionSchema>;

// ============================================================================
// APPLICATION CODE VALIDATION
// ============================================================================

/**
 * Application code validation schema
 * Format: lowercase alphanumeric with hyphens (e.g., "inventory", "crm-system")
 * Used in permission format: <applicationCode>:<action>
 */
export const applicationCodeSchema = z
  .string({
    required_error: 'Application code is required',
    invalid_type_error: 'Application code must be a string',
  })
  .min(2, 'Application code must be at least 2 characters')
  .max(50, 'Application code must not exceed 50 characters')
  .regex(
    /^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/,
    'Application code must be lowercase, start with a letter, and contain only letters, numbers, and hyphens'
  );

export type ApplicationCodeValidation = z.infer<typeof applicationCodeSchema>;

// ============================================================================
// APPLICATION ACTIONS VALIDATION
// ============================================================================

/**
 * Single action validation schema
 * Format: lowercase alphanumeric (e.g., "read", "write", "delete")
 */
export const actionSchema = z
  .string({
    invalid_type_error: 'Action must be a string',
  })
  .min(1, 'Action must be at least 1 character')
  .max(50, 'Action must not exceed 50 characters')
  .regex(
    /^[a-z][a-z0-9_-]*$/,
    'Action must be lowercase and contain only letters, numbers, hyphens, and underscores'
  );

/**
 * Actions array validation schema
 * Non-empty array of valid action strings
 */
export const actionsArraySchema = z
  .array(actionSchema, {
    required_error: 'Actions are required',
    invalid_type_error: 'Actions must be an array',
  })
  .min(1, 'At least one action is required')
  .max(50, 'Maximum 50 actions allowed');

export type ActionsArrayValidation = z.infer<typeof actionsArraySchema>;

// ============================================================================
// APPLICATION CREATE VALIDATION
// ============================================================================

/**
 * Application creation schema
 * Used when creating a new custom application
 *
 * Required fields:
 * - name: Unique name for the application
 * - applicationCode: Unique code for permissions (immutable after creation)
 * - actions: Array of supported actions
 *
 * Optional fields:
 * - description: Application description
 * - isActive: Activation control (default: true)
 * - menu: Menu category for organization
 * - submenu: Submenu category for organization
 */
export const applicationCreateSchema = z
  .object({
    applicationName: nonEmptyStringSchema
      .min(1, 'Application name must be at least 1 character long')
      .max(100, 'Application name must not exceed 100 characters'),
    applicationCode: applicationCodeSchema,
    actions: actionsArraySchema,
    description: descriptionSchema,
    isActive: z.boolean().optional(),
    showInLauncher: z.boolean().optional(), // Default: true - controls visibility in launcher
    menu: z
      .string()
      .min(1, 'Menu must be at least 1 character')
      .max(50, 'Menu must not exceed 50 characters')
      .optional(),
    submenu: z
      .string()
      .min(1, 'Submenu must be at least 1 character')
      .max(50, 'Submenu must not exceed 50 characters')
      .optional(),
    roleId: z
      .string()
      .uuid('Invalid role ID format')
      .optional(),
  })
  .strict();

export type ApplicationCreateValidation = z.infer<typeof applicationCreateSchema>;

// ============================================================================
// APPLICATION UPDATE VALIDATION
// ============================================================================

/**
 * Application update schema
 * Used for updating application information
 *
 * Note: applicationCode is immutable and cannot be updated
 * All fields are optional (at least one must be provided)
 */
export const applicationUpdateSchema = z
  .object({
    applicationName: nonEmptyStringSchema
      .min(1, 'Application name must be at least 1 character long')
      .max(100, 'Application name must not exceed 100 characters')
      .optional(),
    actions: actionsArraySchema.optional(),
    description: descriptionSchema,
    functionArn: z
      .string({
        invalid_type_error: 'Function ARN must be a string',
      })
      .regex(/^arn:aws:lambda:[a-z0-9-]+:\d{12}:function:[a-zA-Z0-9-_]+$/, 'Invalid Lambda function ARN format')
      .optional(),
    apiEndpoint: urlSchema.optional(),
    deployedAt: z
      .string({
        invalid_type_error: 'Deployed at must be a string',
      })
      .datetime('Invalid ISO 8601 timestamp format')
      .optional(),
    isActive: z.boolean().optional(),
    showInLauncher: z.boolean().optional(), // Controls visibility in launcher UI
    menu: z
      .string()
      .min(1, 'Menu must be at least 1 character')
      .max(50, 'Menu must not exceed 50 characters')
      .optional(),
    submenu: z
      .string()
      .min(1, 'Submenu must be at least 1 character')
      .max(50, 'Submenu must not exceed 50 characters')
      .optional(),
    currentDeploymentId: z
      .string()
      .uuid('Invalid deployment ID format')
      .optional(),
    executionMode: executionModeSchema.optional(),
    roleId: z
      .string()
      .uuid('Invalid role ID format')
      .nullable()
      .optional(),
  })
  .strict()
  .refine((data) => Object.keys(data).length > 0, 'At least one field must be provided for update');

export type ApplicationUpdateValidation = z.infer<typeof applicationUpdateSchema>;

// ============================================================================
// APPLICATION QUERY VALIDATION
// ============================================================================

/**
 * Application list query parameters schema
 */
export const applicationListQuerySchema = z
  .object({
    status: applicationDeploymentStatusSchema.optional(),
    search: z
      .string({
        invalid_type_error: 'Search must be a string',
      })
      .min(1, 'Search term must be at least 1 character')
      .max(100, 'Search term must not exceed 100 characters')
      .optional(),
    limit: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().min(1).max(100))
      .optional(),
  })
  .strict();

export type ApplicationListQueryValidation = z.infer<typeof applicationListQuerySchema>;

// ============================================================================
// APPLICATION LOGS QUERY VALIDATION
// ============================================================================

/**
 * Application logs query parameters schema
 * Used for fetching CloudWatch logs for an application
 */
export const applicationLogsQuerySchema = z
  .object({
    startTime: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().positive())
      .optional(),
    endTime: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().positive())
      .optional(),
    limit: z
      .string()
      .transform((val) => parseInt(val, 10))
      .pipe(z.number().int().min(1).max(1000))
      .optional(),
    filterPattern: z
      .string({
        invalid_type_error: 'Filter pattern must be a string',
      })
      .max(1024, 'Filter pattern must not exceed 1024 characters')
      .optional(),
  })
  .strict()
  .refine((data) => {
    if (data.startTime && data.endTime) {
      return data.startTime < data.endTime;
    }
    return true;
  }, 'Start time must be before end time');

export type ApplicationLogsQueryValidation = z.infer<typeof applicationLogsQuerySchema>;

// ============================================================================
// APPLICATION INVOCATION VALIDATION
// ============================================================================

/**
 * Application invocation schema
 * Used for invoking application functions for testing
 */
export const applicationInvocationSchema = z
  .object({
    functionName: z
      .string({
        required_error: 'Function name is required',
        invalid_type_error: 'Function name must be a string',
      })
      .min(1, 'Function name cannot be empty')
      .max(100, 'Function name must not exceed 100 characters'),
    payload: z.record(z.unknown()).optional(),
  })
  .strict();

export type ApplicationInvocationValidation = z.infer<typeof applicationInvocationSchema>;
